
import { put, call, takeEvery, takeLatest, select } from 'redux-saga/effects'
import * as actions from './actions'
import { Endpoints } from 'services/api';
import errorHandler from 'common/errorHandler';
import _ from 'lodash';

const getTenant = (state) => state.userLogin.tenant;
const getUser = (state) => state.userLogin.user;

// The list endpoint returns one page and a pageKey when more rows remain. The table shows every key,
// so follow the cursor to the end and hand the caller a single combined result.
function* getApiKeys(api) {
    let items = [];
    let pageKey = null;
    do {
        let params = {};
        if (pageKey) {
            params.pageKey = pageKey;
        }
        let result = yield call([api, api.get], `${Endpoints.users}/api/v1/apikeys`, {}, { params });
        let page = result.data;
        if (page && page.Items && page.Items.length > 0) {
            items = items.concat(page.Items);
        }
        pageKey = (page && page.pageKey) || null;
    }
    while (pageKey);
    return { Items: items, Count: items.length };
}

function* createApiKey(api, apiKey) {
    let result = yield call([api, api.post], `${Endpoints.users}/api/v1/apikeys`, apiKey);
    return result.data;
}

function* getApiKey(api, apiKeyId) {
    try {
        let result = yield call([api, api.get], `${Endpoints.users}/api/v1/apikeys/${apiKeyId}`);
        return result.data;
    }
    catch (error) {
        if (error.isAxiosError) {
            if (error.response && error.response.status === 404) {
                return null;
            }
        }
        throw error;
    }
}

function* updateApiKey(api, apiKeyId, updatedApiKey) {
    let response = yield call([api, api.put], `${Endpoints.users}/api/v1/apikeys/${apiKeyId}`, updatedApiKey);
    return response.data;
}

function* deleteApiKey(api, apiKeyId) {
    yield call([api, api.delete], `${Endpoints.users}/api/v1/apikeys/${apiKeyId}`);
}

// API keys are guarded by the "apikeys" permission resource, seeded per tenant by the tenant service.
export function* getApiKeyPermission(api, tenantId, userId) {
    let result = yield call([api, api.get], `${Endpoints.permissions}/api/v1/users/${userId}/permissions/resources/apikeys`, { tenantid: tenantId });
    return result.data;
}

export function* getApiKeysRequest(api) {
    try {
        const result = yield call(getApiKeys, api);
        yield put(actions.getApiKeysSuccess(result));
    } catch (error) {
        let errorObject = errorHandler(error, "Get", "api keys");
        yield put(actions.getApiKeysFailure(errorObject));
    }
}

export function* createApiKeyRequest(api, { apiKey }) {
    try {
        let createdApiKey = yield call(createApiKey, api, apiKey);
        yield put(actions.createApiKeySuccess(createdApiKey));
    } catch (error) {
        let errorObject = errorHandler(error, "Create", "api key");
        yield put(actions.createApiKeyFailure(errorObject));
    }
}

export function* getApiKeyRequest(api, { apiKeyId }) {
    try {
        const apiKey = yield call(getApiKey, api, apiKeyId);
        yield put(actions.getApiKeySuccess(apiKey));
    } catch (error) {
        let errorObject = errorHandler(error, "Get", "api key");
        yield put(actions.getApiKeyFailure(errorObject));
    }
}

export function* updateApiKeyRequest(api, { apiKeyId, updatedApiKey }) {
    try {
        const apiKey = yield call(updateApiKey, api, apiKeyId, updatedApiKey);
        yield put(actions.updateApiKeySuccess(apiKey));
    } catch (error) {
        let errorObject = errorHandler(error, "Update", "api key");
        yield put(actions.updateApiKeyFailure(errorObject));
    }
}

export function* deleteApiKeyRequest(api, { apiKeyId }) {
    try {
        yield call(deleteApiKey, api, apiKeyId);
        yield put(actions.deleteApiKeySuccess(apiKeyId));
    } catch (error) {
        let errorObject = errorHandler(error, "Delete", "api key");
        yield put(actions.deleteApiKeyFailure(errorObject));
    }
}

export function* getApiKeyPermissionRequest(api) {
    try {
        let permission = { canAdd: false, canView: false, canEdit: false, canDelete: false };
        let tenant = yield select(getTenant);
        let user = yield select(getUser);
        let permissionResponse = yield call(getApiKeyPermission, api, tenant.tenantId, user.userId);
        if (permissionResponse && permissionResponse.Items && permissionResponse.Items.length > 0) {
            let permissionActions = permissionResponse.Items.map(item => item.actions);
            permissionActions = _.union(...permissionActions);
            if (permissionActions.length > 0) {
                if (permissionActions.includes("add")) {
                    permission.canAdd = true;
                }
                if (permissionActions.includes("view")) {
                    permission.canView = true;
                }
                if (permissionActions.includes("edit")) {
                    permission.canEdit = true;
                }
                if (permissionActions.includes("delete")) {
                    permission.canDelete = true;
                }
            }
        }

        yield put(actions.getApiKeyPermissionSuccess(permission));
    } catch (error) {
        let errorObject = errorHandler(error, "Get", "user api key permission");
        yield put(actions.getApiKeyPermissionFailure(errorObject));
    }
}

export function* watchGetApiKeysRequest(api, params) {
    yield call(getApiKeysRequest, api);
}

export function* watchCreateApiKeyRequest(api, { params }) {
    yield call(createApiKeyRequest, api, params);
}

export function* watchGetApiKeyRequest(api, { params }) {
    yield call(getApiKeyRequest, api, params);
}

export function* watchUpdateApiKeyRequest(api, { params }) {
    yield call(updateApiKeyRequest, api, params);
}

export function* watchDeleteApiKeyRequest(api, { params }) {
    yield call(deleteApiKeyRequest, api, params);
}

export function* watchGetApiKeyPermissionRequest(api, { params }) {
    yield call(getApiKeyPermissionRequest, api);
}

export default function* ({ api }) {
    yield takeLatest(actions.GET_API_KEYS_REQUEST, watchGetApiKeysRequest, api);
    yield takeEvery(actions.CREATE_API_KEY_REQUEST, watchCreateApiKeyRequest, api);
    yield takeLatest(actions.GET_API_KEY_REQUEST, watchGetApiKeyRequest, api);
    yield takeLatest(actions.UPDATE_API_KEY_REQUEST, watchUpdateApiKeyRequest, api);
    yield takeLatest(actions.DELETE_API_KEY_REQUEST, watchDeleteApiKeyRequest, api);
    yield takeEvery(actions.GET_API_KEY_PERMISSIONS_REQUEST, watchGetApiKeyPermissionRequest, api);
}
